import { useTranslations } from "@/lib/useTranslations";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";

import { Button } from "@/components/ui/Button";
import { FieldLabel } from "@/components/ui/FieldLabel";
import {
  ARTICLE_COVER_IMAGE_ACCEPTED_TYPES,
  ARTICLE_COVER_IMAGE_MAX_BYTES,
  isAcceptedCoverImageType,
} from "@/core/services/types/media.types";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

interface ArticleCoverImageFieldProps {
  savedUrl: string | null;
  required?: boolean;
  disabled: boolean;
  isPending: boolean;
  saveSpinner: ReactNode;
  fieldError?: string;
  error?: string;
  success: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

export function ArticleCoverImageField({
  savedUrl,
  required = false,
  disabled,
  isPending,
  saveSpinner,
  fieldError,
  error,
  success,
  onUpload,
  onRemove,
}: ArticleCoverImageFieldProps) {
  const t = useTranslations("articles.edit");
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  const previewUrl = localPreviewUrl ?? savedUrl;
  const hasSavedImage = Boolean(savedUrl?.trim());
  const acceptedTypes = ARTICLE_COVER_IMAGE_ACCEPTED_TYPES.join(",");

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  useEffect(() => {
    if (success && localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
  }, [success, localPreviewUrl]);

  function validateAndUpload(file: File) {
    setClientError(null);

    if (!isAcceptedCoverImageType(file.type)) {
      setClientError(t("coverImageInvalidType"));
      return;
    }

    if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
      setClientError(t("coverImageTooLarge"));
      return;
    }

    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }

    setLocalPreviewUrl(URL.createObjectURL(file));
    onUpload(file);
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setIsDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = event.dataTransfer.files.item(0);
    if (!file) return;
    validateAndUpload(file);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.item(0);
    event.target.value = "";
    if (!file) return;
    validateAndUpload(file);
  }

  function handleBrowseClick() {
    if (disabled) return;
    inputRef.current?.click();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleBrowseClick();
    }
  }

  return (
    <section className="space-y-3">
      <FieldLabel as="p" id={`${inputId}-label`} required={required}>
        {t("coverImageLabel")}
      </FieldLabel>

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={`${inputId}-label`}
        aria-describedby={`${inputId}-hint`}
        aria-busy={isPending}
        onClick={handleBrowseClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={[
          "wireframe-border relative overflow-hidden bg-neutral-50 transition-colors",
          isDragging ? "border-whart-dark bg-white" : "",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        ].join(" ")}
      >
        {previewUrl ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previewUrl}
              alt={t("coverImagePreviewAlt")}
              className="aspect-[16/9] w-full object-cover"
            />
            {isPending ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                <span className="inline-flex items-center gap-2 text-sm text-whart-text">
                  {saveSpinner}
                  {t("coverImageUploading")}
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex aspect-[16/9] flex-col items-center justify-center gap-2 px-6 text-center">
            {isPending ? (
              <span className="inline-flex items-center gap-2 text-sm text-whart-text">
                {saveSpinner}
                {t("coverImageUploading")}
              </span>
            ) : (
              <>
                <p className="text-sm text-whart-text">{t("coverImageDropTitle")}</p>
                <p id={`${inputId}-hint`} className="text-xs text-neutral-400">
                  {t("coverImageDropHint")}
                </p>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={acceptedTypes}
          disabled={disabled}
          className="sr-only"
          onChange={handleInputChange}
        />
      </div>

      <FieldError message={clientError ?? fieldError} />

      {hasSavedImage ? (
        <div>
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || isPending}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            {t("coverImageRemove")}
          </Button>
        </div>
      ) : null}

      {!success && error ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {error}
        </p>
      ) : null}

      {success ? (
        <p role="status" className="text-xs text-emerald-700">
          {t("saveCoverImageSuccess")}
        </p>
      ) : null}
    </section>
  );
}
